import React, { useState, useEffect, useCallback } from 'react'
import EditarClienteForm from '../components/EditarClienteForm'
import DuplicateResolveDialog from '../components/DuplicateResolveDialog'
import EditConflictTransferDialog from '../components/EditConflictTransferDialog'
import { apiFetch } from '../../api'
import {  sanitizeMoneyPayload } from '../../utils/moneyAR'

export default function EditarClienteContainer({
    openControlled = undefined,
    onCloseControlled = undefined,
    cliente = null
}) {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [duplicateOpen, setDuplicateOpen] = useState(false)
    const [duplicatePayload, setDuplicatePayload] = useState(null)
    const [editConflictOpen, setEditConflictOpen] = useState(false)
    const [editConflictPayload, setEditConflictPayload] = useState(null)


    // Inicializar formulario con datos del cliente
    const getInitialForm = useCallback(() => {
        if (cliente) {
            return {
                nombre: cliente.nombre || '',
                telefono: cliente.telefono || '',
                email: cliente.email || '',
                consulta: cliente.consulta || '',
                consulta2: cliente.consulta2 || '',
                entrega: cliente.entrega || '',
                precio_de_toma: cliente.precio_de_toma || '',
                obtenido_de: cliente.obtenido_de || '',
            }
        }
        return {
            nombre: '',
            telefono: '',
            email: '',
            consulta: '',
            consulta2: '',
            entrega: '',
            precio_de_toma: '',
            obtenido_de: '',
        }
    }, [cliente])

    const [form, setForm] = useState(() => getInitialForm())

    useEffect(() => {
        setForm(getInitialForm())
    }, [getInitialForm])

    const isControlled = openControlled !== undefined
    const isOpen = isControlled ? openControlled : false

    // Función para cerrar sin éxito (cancelación)
    const handleClose = () => {
        if (isControlled) {
            onCloseControlled?.(false)
        }
        setError(null)
        setForm(getInitialForm())
    }

    // Función para cerrar con éxito (actualización exitosa)
    const handleCloseSuccess = () => {
        if (isControlled) {
            onCloseControlled?.(true)
        }
        setError(null)
        setForm(getInitialForm())
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setForm(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!cliente?.id) {
            setError('No se puede actualizar: cliente no válido')
            return
        }

        setLoading(true)
        setError(null)

        try {
            const cleanedForm = {
                ...form,
                email: form.email.trim() === '' ? null : form.email?.trim(),
                precio_de_toma: sanitizeMoneyPayload(form.precio_de_toma),
            }

            const res = await apiFetch(`/api/clientes/${cliente.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cleanedForm),
                credentials: 'include'
            })

            if (!res.ok) {
                // If backend returned a duplicate payload (409), open the duplicate resolver dialog
                if (res.status === 409) {
                    let json = null;
                    try { json = await res.json(); } catch(e) { json = null; }

                                                                    if (json && json.origin === 'edit') {
                                                                        setEditConflictPayload(json);
                                                                        setEditConflictOpen(true);
                                                                    } else {
                                                                        setDuplicatePayload(json);
                                                                        setDuplicateOpen(true);
                                                                    }
                    setLoading(false);
                    return;
                }

                let errorData;
                try {
                    errorData = await res.json()
                } catch (parseError) {
                    errorData = { detail: `HTTP ${res.status}: ${res.statusText}` }
                }

                // Mejorar el manejo de errores
                let errorMessage = `HTTP ${res.status}`;

                if (errorData) {
                    if (typeof errorData === 'string') {
                        errorMessage = errorData;
                    } else if (errorData.detail) {
                        if (typeof errorData.detail === 'string') {
                            errorMessage = errorData.detail;
                        } else if (Array.isArray(errorData.detail)) {
                            errorMessage = errorData.detail.map(err =>
                                typeof err === 'string' ? err :
                                    err.msg || err.message || JSON.stringify(err)
                            ).join(', ');
                        } else {
                            errorMessage = JSON.stringify(errorData.detail);
                        }
                    } else if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.errors) {
                        // Para errores de validación de FastAPI
                        errorMessage = Object.entries(errorData.errors)
                            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
                            .join('; ');
                    } else {
                        errorMessage = JSON.stringify(errorData);
                    }
                }

                throw new Error(errorMessage)
            }
            handleCloseSuccess()

        } catch (err) {
            // Asegurar que el error sea un string legible
            let errorMessage = 'Error actualizando el cliente';

            if (err.message && typeof err.message === 'string') {
                errorMessage = err.message;
            } else if (typeof err === 'string') {
                errorMessage = err;
            } else {
                errorMessage = err.toString();
            }

            setError(errorMessage)
        } finally {
            setLoading(false)
        }
    }
    // Render the edit form and the duplicate resolver dialog (if needed)
    return (
        <>
            <EditarClienteForm
                open={isOpen}
                onClose={handleClose}
                form={form}
                onChange={handleChange}
                onSubmit={handleSubmit}
                loading={loading}
                error={error}
            />

            <DuplicateResolveDialog
                open={duplicateOpen}
                onClose={() => setDuplicateOpen(false)}
                existing={duplicatePayload?.existing}
                incoming={duplicatePayload?.incoming}
                match_source={duplicatePayload?.match_source}
                onResolved={({ kept, created }) => {
                    setDuplicateOpen(false);
                    // if resolved by creating incoming, consider the update handled: close modal
                    handleCloseSuccess();
                }}
            />
            <EditConflictTransferDialog
                open={editConflictOpen}
                onClose={() => { setEditConflictOpen(false); setEditConflictPayload(null); }}
                existing={editConflictPayload?.existing}
                incoming={editConflictPayload?.incoming}
                resolution={editConflictPayload?.resolution}
                target={cliente}
                onResolved={({ kept, created }) => {
                    setEditConflictOpen(false);
                    setEditConflictPayload(null);
                    handleCloseSuccess();
                }}
            />
        </>
    )
}